import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import Logo from './Logo'
import ThemeToggle from './ThemeToggle'
import Button from './Button'
import { duration, ease } from '../lib/motion'
import { resolveSectionHref } from '../lib/sectionAnchors'
import { useFormModal } from '../context/FormModalContext'

// anchor: the section id to resolve via resolveSectionHref (see
// lib/sectionAnchors.js for exactly which pages have which sections —
// don't assume here, that file is the single source of truth).
const LINKS = [
  { anchor: 'transformations', label: 'Work' },
  { href: '/services', label: 'Services' },
  { href: '/about', label: 'About' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/insights', label: 'Insights' },
  { href: '/audit', label: 'Free Audit' },
  { href: '/academy', label: 'Academy' },
  { href: '/partner', label: 'Partners' },
  { href: '/faq', label: 'FAQ' },
]

export default function Navbar() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const { openInquiry } = useFormModal()
  // #top always goes home first when not already there — same reasoning
  // as resolveSectionHref, kept separate since "top" isn't a real section.
  const homeOnlyAnchor = (href) => (location.pathname === '/' ? href : `/${href}`)
  const linkHref = (l) => (l.anchor ? resolveSectionHref(l.anchor, location.pathname) : l.href)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: scrolled ? 'color-mix(in srgb, var(--bg) 88%, transparent)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      }}
    >
      <div className="container-bw flex items-center justify-between h-[76px]">
        <a href={homeOnlyAnchor('#top')} className="flex items-center gap-3 shrink-0">
          <Logo className="h-8 w-8 rounded-md shrink-0" />
          <span className="hidden sm:inline font-display text-[15px] tracking-tight whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
            Better Website Co.
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={linkHref(l)}
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <ThemeToggle />
          <Button onClick={() => openInquiry()} variant="primary" ttqTrack>
            Start My Website
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <ThemeToggle />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: duration.fast, ease: ease.out }}
            className="lg:hidden overflow-hidden"
            style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}
          >
            <div className="container-bw py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={linkHref(l)}
                  onClick={() => setOpen(false)}
                  className="text-base"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {l.label}
                </a>
              ))}
              <Button
                variant="primary"
                ttqTrack
                onClick={() => {
                  setOpen(false)
                  openInquiry()
                }}
              >
                Start My Website
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Mobile bottom tab bar — the "app-like" primary navigation for
          mobile specifically; desktop keeps the normal inline nav above,
          completely untouched. Five tabs cover the actions that matter
          most: Home, Work (proof), Pricing, Start (the real conversion —
          scrolls to the on-page inquiry form; lower-commitment than a
          scheduled call, see Hero.jsx/FinalCTA.jsx for the reasoning),
          and More (opens the same slide-down panel above, just triggered
          from here instead of a top hamburger — the hamburger button
          itself was removed since this replaces its job). Minimal inline
          SVG icons, not an icon library — matches how the rest of the
          site already avoids external icon dependencies (arrows/marks
          elsewhere are plain unicode glyphs). Fixed to the viewport
          bottom; Footer.jsx reserves matching space on mobile so it
          never overlaps real content. */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-stretch"
        style={{
          background: 'color-mix(in srgb, var(--bg) 92%, transparent)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid var(--border)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        aria-label="Primary"
      >
        <TabLink href={homeOnlyAnchor('#top')} label="Home" active={location.pathname === '/'}>
          <path d="M3 9.5 10 4l7 5.5V16a1 1 0 0 1-1 1h-3.5v-4.5h-5V17H4a1 1 0 0 1-1-1V9.5Z" />
        </TabLink>
        <TabLink href={resolveSectionHref('transformations', location.pathname)} label="Work">
          <rect x="3" y="4" width="6" height="6" rx="1" />
          <rect x="11" y="4" width="6" height="6" rx="1" />
          <rect x="3" y="12" width="6" height="6" rx="1" />
          <rect x="11" y="12" width="6" height="6" rx="1" />
        </TabLink>
        <TabLink href="/pricing" label="Pricing" active={location.pathname === '/pricing'}>
          <path d="M10 3 3 10v4l0 0 3 3h4l7-7V4a1 1 0 0 0-1-1h-6Z" />
          <circle cx="13.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </TabLink>
        <TabLink onClick={() => openInquiry()} label="Start">
          <rect x="4" y="3" width="12" height="14" rx="1.5" />
          <path d="M7 7h6M7 10h6M7 13h3" />
        </TabLink>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Close menu' : 'More'}
          aria-expanded={open}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-2.5"
          style={{ color: open ? 'var(--accent)' : 'var(--text-muted)' }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="4" cy="10" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="10" cy="10" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="16" cy="10" r="1.3" fill="currentColor" stroke="none" />
          </svg>
          <span className="text-[10px] font-medium">More</span>
        </button>
      </nav>
    </header>
  )
}

// Renders as a real link when given href (normal page navigation/anchor
// scroll), or as a button when given onClick instead (the "Start" tab,
// which opens the popup rather than navigating anywhere).
function TabLink({ href, onClick, label, children, active = false, external = false }) {
  const content = (
    <>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
      <span className="text-[10px] font-medium">{label}</span>
    </>
  )
  const sharedProps = {
    className: 'flex-1 flex flex-col items-center justify-center gap-1 py-2.5',
    style: { color: active ? 'var(--accent)' : 'var(--text-muted)' },
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} {...sharedProps}>
        {content}
      </button>
    )
  }
  return (
    <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} {...sharedProps}>
      {content}
    </a>
  )
}
